import { generateColorCssVariable, SharedUiColorName, SharedUiColorsService } from '@/shared/ui/theming';
import { UiButtonComponent } from '@/ui/button/ui-button';
import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractValue } from '@/utils/rxjs/helpers/rxjs.helpers';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import type { Options } from 'highcharts';
import { combineLatest, never, type Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { darkColor } from '../../constants/color';
import type { RecordedEstimatedData } from '../../interfaces/GenericData.interface';
import type { UnitFormatter } from '../../interfaces/Unit.interface';
import type { LandingSummaryConfig } from '../../interfaces/Widget.interface';
import { FormatUnitPipe } from '../../pipes/format-unit.pipe';
import { InterpolatePipe } from '../../pipes/interpolate/interpolate.pipe';
import { LocalNumberPipe, SignificantDigitPipe } from '../../pipes/localnumber.pipe';
import { BudgetsProvider } from '../../providers/budgets/budgets.provider';
import { DashboardingProvider } from '../../providers/dashboarding.provider';
import { DataProvider } from '../../providers/data/data.provider';
import { HighchartsStaticProvider } from '../../providers/highcharts/highcharts.static.provider';
import { UnitProvider } from '../../providers/unit/unit.provider';
import { WidgetProvider } from '../../providers/widget.provider';
import { WidgetDataSource } from '../../providers/widgets.contants';
import { Page, RoutingService } from '../../services/routing/routing.service';
import { DeveloperHelperComponent } from '../developer-helper/developer-helper';
import { HighchartsChartComponent } from '../highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '../loading-and-error/loading-and-error';
import { WidgetCardComponent } from '../widgets/widget-card/widget-card';
import { OldDataSourceCode } from './../../services/developer-helper.service';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./landing-summary.ng-cli.scss'],
    selector: 'landing-summary',
    templateUrl: 'landing-summary.html',
    imports: [
        WidgetCardComponent,
        DeveloperHelperComponent,
        HighchartsChartComponent,
        SvgIconComponent,
        UiButtonComponent,
        LoadingAndErrorComponent,
        AsyncPipe,
        TranslatePipe,
        LocalNumberPipe,
        SignificantDigitPipe,
        InterpolatePipe,
        FormatUnitPipe,
        ApplyPureFunctionPipe
    ]
})
export class LandingSummaryComponent implements OnInit {
    private _dataProvider = inject(DataProvider);
    private _budgetsProvider = inject(BudgetsProvider);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _routingService = inject(RoutingService);
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _unitProvider = inject(UnitProvider);
    private _widgetsProvider = inject(WidgetProvider);
    private _colorsService = inject(SharedUiColorsService);

    @Input() widgetConfig!: LandingSummaryConfig;
    @Input() actionLabel!: string;
    @Output() actionClick: EventEmitter<void> = new EventEmitter();

    widgetDataSourceEnum = WidgetDataSource;

    rawData: RecordedEstimatedData | null = null;

    computedData: RecordedEstimatedData | null = null;
    savings: number | null = null;
    savingsPercent: number | null = null;

    isLoading: boolean = true;
    loadingError: boolean = false;

    chartConfig$!: Observable<Options>;

    isCostTimeseriesIndicator: boolean = false;

    dataSourceCode = OldDataSourceCode.DP_TIMESERIES_MONTHLY;

    valueFormatter: UnitFormatter = (): string => '';
    savingsValueFormatter: UnitFormatter = (): string => '';

    canAccessToBudget$ = this._budgetsProvider.canAccessToBudget$();

    ngOnInit() {
        this.isCostTimeseriesIndicator =
            this.widgetConfig.dataSource === WidgetDataSource.TIMESERIES &&
            this._widgetsProvider.isIndicatorCodeCost(this.widgetConfig.timeseriesIndicator);

        this.watchDeliveryPointTimeseriesData();
    }

    resetLoading() {
        this.rawData = null;
        this.computedData = null;
        this.savings = null;
        this.savingsPercent = null;
        this.isLoading = true;
        this.loadingError = false;
    }

    watchDeliveryPointTimeseriesData() {
        combineLatest([
            this._dashboardingProvider.getMeaningfulNodeIdAndSelectedPeriod$(),
            this._dashboardingProvider.isEstimatedIgnoredOnWidgets$()
        ])
            .pipe(
                tap(() => this.resetLoading()),
                switchMap(([[classificationNodeId, period], ignoreEstimated]) => {
                    const normalizedPeriod = this._dashboardingProvider.normalizePeriodTo12FullMonths(period);
                    return this._dataProvider.getUniverseAggregatedRecordedEstimatedData$(
                        { classificationNodeId, indicatorCodes: [this.widgetConfig.timeseriesIndicator] },
                        normalizedPeriod.start,
                        normalizedPeriod.end,
                        ignoreEstimated
                    );
                }),
                catchError(error => {
                    console.error('error while fetching timeseries in landing-summary widget::', error);
                    this.isLoading = false;
                    this.loadingError = true;
                    return never();
                }),
                untilDestroyed(this)
            )
            .subscribe(byUniverseMap => {
                this.isLoading = false;
                this.rawData = this._dataProvider.sumRecordedEstimatedData(Array.from(byUniverseMap.values()));
                this.buildDisplayedDataAndChart();
            });
    }

    buildDisplayedDataAndChart() {
        if (!this.rawData) return;

        this.computedData = {
            ...this.rawData
        };

        if (this.computedData) {
            this.savings = (this.computedData.reference ?? 0) - ((this.computedData.recorded ?? 0) + (this.computedData.estimated ?? 0));
            this.savingsPercent = (this.savings * 100) / (this.computedData.reference ?? 0);
        }

        this.initializeFormattersForTimeseries(this.computedData, this.savings);

        this.configureColumnChart();
        this.isLoading = false;
    }

    getBudgetColor$ = (savings: number | null): Observable<string> => {
        if (!savings) return this._colorsService.getColoredTextColor$(SharedUiColorName.NEUTRAL);
        return savings >= 0
            ? this._colorsService.getColoredTextColor$(SharedUiColorName.SUCCESS)
            : this._colorsService.getColoredTextColor$(SharedUiColorName.DANGER);
    };

    initializeFormattersForTimeseries(data: RecordedEstimatedData | null, savings: number | null) {
        if (!data || !savings) {
            return;
        }
        const baseUnitCode = this._unitProvider.getDeliveryPointTimeseriesUnitCode(this.widgetConfig.timeseriesIndicator);

        this.valueFormatter = this._unitProvider.formatterFactory(
            baseUnitCode,
            this._unitProvider.getBestConversion([(data.recorded ?? 0) + (data.estimated ?? 0), data.reference ?? 0], baseUnitCode)
        );
        this.savingsValueFormatter = this._unitProvider.formatterFactory(
            baseUnitCode,
            this._unitProvider.getBestConversion([savings], baseUnitCode)
        );

        if (this._widgetsProvider.isIndicatorCodeCost(this.widgetConfig.timeseriesIndicator)) {
            this.valueFormatter = this._budgetsProvider.enhanceFormatterWithCurrentVatMode(this.valueFormatter);
            this.savingsValueFormatter = this._budgetsProvider.enhanceFormatterWithCurrentVatMode(this.savingsValueFormatter);
        }
    }

    navigateToBudget() {
        this._routingService.goTo(Page.BUDGET_EDITION, {});
    }

    onActionClick() {
        this.actionClick.emit();
    }

    configureColumnChart() {
        if (!this.computedData) return;
        const spendingsColor = `var(${generateColorCssVariable(SharedUiColorName.SECONDARY)})`;

        const valueFormatter = this.valueFormatter;
        this.chartConfig$ = combineLatestDeepObject(
            this._highchartsStaticProvider.buildConfig(this._highchartsStaticProvider.COLUMN_MIN_SIZE_5, {
                chart: {
                    type: 'column',
                    crop: false,
                    marginTop: 20,
                    height: 250
                },
                title: {
                    text: null
                },
                xAxis: {
                    categories: [this.widgetConfig.translations.consumption, this.widgetConfig.translations.reference],
                    visible: true,
                    lineColor: 'transparent',
                    labels: {
                        style: {
                            fontWeight: 'bold',
                            fontSize: '12px'
                        }
                    }
                },
                yAxis: {
                    min: Math.min(
                        (this.computedData.recorded ?? 0) + (this.computedData.estimated ?? 0),
                        this.computedData.reference ?? 0,
                        this.computedData.estimated ?? 0,
                        this.computedData.recorded ?? 0,
                        0
                    ),
                    max: Math.max(
                        (this.computedData.recorded ?? 0) + (this.computedData.estimated ?? 0),
                        this.computedData.reference ?? 0,
                        this.computedData.estimated ?? 0,
                        this.computedData.recorded ?? 0,
                        0
                    ),
                    endOnTick: false,
                    stackLabels: {
                        enabled: true,
                        style: {
                            fontWeight: 'bold',
                            color: darkColor,
                            fontSize: '12px'
                        },
                        overflow: 'allow',
                        crop: false,
                        formatter(this: TooltipFormatterContext) {
                            return valueFormatter(this.total ?? 0);
                        }
                    },
                    gridLineWidth: 0,
                    labels: {
                        enabled: false
                    },
                    plotLines: [
                        {
                            color: 'lightgrey',
                            width: 1,
                            value: 0
                        }
                    ]
                },
                plotOptions: {
                    column: {
                        pointPadding: 0.2,
                        borderWidth: 0,
                        stacking: 'normal'
                    }
                },
                tooltip: {
                    outside: true,
                    hideDelay: 100,
                    formatter: () => {
                        let tooltip = '';
                        tooltip += this.computedData?.recorded
                            ? `<span style="color:${spendingsColor}">\u25CF</span>${
                                  this.widgetConfig.translations.recordedConsumption
                              }: <b>${valueFormatter(this.computedData.recorded)}</b><br/>`
                            : '';
                        tooltip += this.computedData?.estimated
                            ? `<span style="color:${spendingsColor}">\u25CF</span>${
                                  this.widgetConfig.translations.estimatedConsumption
                              }: <b>${valueFormatter(this.computedData.estimated)}</b><br/>`
                            : '';
                        tooltip += this.computedData?.reference
                            ? `<span style="color:${extractValue(this.getBudgetColor$(this.savings))}">\u25CF</span>${
                                  this.widgetConfig.translations.reference
                              }: <b>${valueFormatter(this.computedData.reference)}</b><br/>`
                            : '';
                        return tooltip;
                    },
                    useHTML: true
                },
                series: [
                    {
                        type: 'column',
                        data: [
                            { y: this.computedData.recorded, color: spendingsColor },
                            { y: this.computedData.reference, color: this.getBudgetColor$(this.savings) }
                        ],
                        index: (this.computedData.recorded ?? 0) < 0 && (this.computedData.estimated ?? 0) < 0 ? 0 : 1
                    },
                    {
                        type: 'column',
                        data: this.computedData.estimated
                            ? [
                                  {
                                      y: this.computedData.estimated,
                                      color: this._highchartsStaticProvider.generateDiagonalStripesPattern(spendingsColor)
                                  }
                              ]
                            : [],
                        index: (this.computedData.recorded ?? 0) < 0 && (this.computedData.estimated ?? 0) < 0 ? 1 : 0
                    }
                ]
            })
        );
    }
}
